import styled from 'styled-components';
import {
  BackButton,
  borderRadiusCircle,
  borderRadiusMain,
  colorPrimary100Default,
  colorTertiary10Lightest,
  colorTextLinkHover,
  fontFamilyMain,
  fontSize1,
  fontSize2,
  fontSize3,
  HeadingS,
  letterSpacingLarge,
  colorBackgroundDisabled,
  BodyTextSmall,
  breakpointMedium,
} from 'lib/design-system';
import {
  Icon,
  SecondaryButton,
  default700,
  neutral600,
  spacing1,
  spacing2,
  spacing3,
  spacing4,
  spacing6,
  fontWeightSemibold,
  fontWeightRegular,
  default500,
  default200,
  default900,
  borderRadius,
  neutral200,
  breakpointSmallEnd,
  neutral900,
} from 'lib/mortar';
import { getWidth } from 'lib/utils';
import { Stack } from 'components/layout/stack';

export const Wrapper = styled.article`
  @media (max-width: ${breakpointSmallEnd}) {
    margin: ${spacing2} ${spacing4};
  }

  ${getWidth}

  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: center;

  position: relative;
  flex-direction: column;

  > :first-child {
    display: flex;
    flex-direction: column;
    place-content: start;
  }
`;

export const Header = styled.h2<StyledHeading>`
  font-size: ${fontSize3};
  font-weight: ${fontWeightSemibold};
  color: ${default700};
  margin: 0 0 ${spacing3} 0;
`;

export const RadioButton = styled.input.attrs({ type: 'radio' })`
  border: 1px solid ${colorPrimary100Default};
  margin: 0 ${spacing3} 0 0;
  width: 16px;
  height: 16px;
`;

export const Subtitle = styled(BodyTextSmall)`
  margin-bottom: ${spacing4};
`;

export const ButtonText = styled.span<StyledSpan>`
  font-family: ${fontFamilyMain};
  font-weight: ${fontWeightSemibold};
  font-size: ${fontSize2};
  letter-spacing: ${letterSpacingLarge};
  text-align: left;
`;

export const IconWrapper = styled.span<
  StyledSpan & {
    isSelected: boolean;
  }
>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  padding-left: 2px;
  box-sizing: border-box;
  margin-left: ${spacing1};
  border: 1px solid ${neutral600};
  border-radius: ${borderRadiusCircle};
  &:hover {
    border-color: ${neutral600};
  }
`;

export const FunnellingBaseButton = styled.button<
  StyledButton & {
    isSelected?: boolean;
  }
>`
  position: relative;
  width: 100%;
  padding: ${`${spacing3} ${spacing2} ${spacing3} ${spacing4}`};
  margin-top: -1px;

  display: grid;
  grid-template-columns: 1fr 24px;
  grid-column-gap: ${spacing1};

  cursor: pointer;
  text-align: left;
  background-color: ${props =>
    props.isSelected ? colorTextLinkHover : 'initial'};
  border: 1px solid ${colorTertiary10Lightest};
  border-radius: ${borderRadiusMain};

  :hover,
  :active,
  :focus {
    outline: none;
    border-color: ${default700};
    z-index: 1000;

    ${IconWrapper} {
      border-color: ${default500};
    }
    ${Icon} svg path {
      fill: ${default500};
    }
  }
  > span {
    width: 22px;
    height: 22px;
    padding: 4px;
    box-sizing: border-box;
  }
`;

export const ButtonLabel = styled.div<StyledDiv>`
  display: flex;
  flex-direction: column;
  place-items: start;
`;

export const SubHeading = styled.h2<StyledHeading>`
  margin: 0;
  color: ${neutral600};
  font-size: ${fontSize1};
  font-weight: ${fontWeightSemibold};
  margin-top: ${spacing1};
`;

export const Description = styled.div<StyledDiv>`
  color: ${neutral600};
  font-size: ${fontSize1};
  margin-top: ${spacing1};
`;

export const CustomBackButton = styled(BackButton)`
  color: ${neutral600};
  font-size: ${fontSize1};
  font-weight: ${fontWeightRegular};

  ${Icon} {
    height: 12px;
    width: 12px;
  }

  ${Icon} svg path {
    fill: ${neutral600};
  }
`;

export const SelectionButtonContainer = styled.div<StyledDiv>`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${spacing2};
`;

export const FunnellingArticle = styled.article`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: ${spacing2} ${spacing4};

  > ${HeadingS} {
    margin-bottom: ${spacing3};
  }
  > ${SecondaryButton} {
    margin-top: ${spacing6};
  }
`;

export const FunnellingFooter = styled.footer`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
  align-items: flex-start;
`;

export const FunnellingBackButton = styled(BackButton)`
  color: ${neutral600};
  font-size: ${fontSize2};
  font-weight: ${fontWeightRegular};

  ${Icon} {
    height: 12px;
    width: 12px;
  }

  ${Icon} svg path {
    fill: ${neutral600};
  }
`;

export const FunnellingContinueButton = styled(FunnellingBaseButton)`
  color: ${neutral600};
  font-size: ${fontSize2};
  font-weight: ${fontWeightRegular};

  ${Icon} {
    height: 12px;
    width: 12px;
  }

  ${Icon} svg path {
    fill: ${neutral600};
  }
`;

export const CenteredContainer = styled.div<StyledDiv>`
  display: flex;
  place-content: center;
  flex-grow: 1;
`;

export const WrapperVerificationEmail = styled.div<StyledDiv>`
  background-color: ${default200};
  border-radius: ${borderRadius(2)};
  padding: ${spacing3};

  & > ${BodyTextSmall}:first-of-type {
    margin-bottom: ${spacing6};
  }
  @media (min-width: ${breakpointSmallEnd}) {
    padding: ${spacing6};
  }
`;

export const InnerVerificationEmail = styled.ul<StyledUnorderedList>`
  display: flex;
  flex-direction: column;
  gap: ${spacing4};
  padding: 0 ${spacing4};
`;

export const InnerBlockVerificationEmail = styled.li<StyledListItem>`
  display: flex;
  gap: ${spacing2};
`;

export const ContentVerificationEmail = styled.div<StyledDiv>`
  display: flex;
  flex-direction: column;
  gap: ${spacing1};

  ${HeadingS} {
    font-weight: ${fontWeightSemibold};
    color: ${neutral900};
  }
`;

export const SuccessAlert = styled.div<StyledDiv>`
  font-weight: ${fontWeightSemibold};
  align-items: center;
  display: flex;
  margin-bottom: 20px;
  ${Icon} {
    margin-right: 10px;
  }
`;

export const textAreaStyle = {
  minHeight: 'none',
  height: 170,
};

export const HorizontalLine = styled.hr`
  border: none;
  border-top: 1px solid ${colorBackgroundDisabled};
  margin: 0 0 18px 0px;
  padding: 0;
`;

export const QuestionTitle = styled.div<StyledDiv>`
  color: ${neutral600};
  font-size: 0.75rem;
  margin-bottom: ${spacing2};

  @media (min-width: ${breakpointMedium.start}) {
    font-size: ${fontSize1};
  }
`;

export const QuestionAnswer = styled.div<StyledDiv>`
  font-size: ${fontSize1};
  font-weight: ${fontWeightSemibold};
  margin-bottom: ${spacing4};

  @media (min-width: ${breakpointMedium.start}) {
    font-size: ${fontSize2};
  }
`;

export const DeadEndArticle = styled(FunnellingArticle)`
  gap: ${spacing3};
  > ${HeadingS} {
    margin: 0;
    line-height: 1.3rem;
  }

  padding-left: 0;
  padding-right: 0;

  ${FunnellingFooter} {
    padding: ${spacing3} 0;
  }
`;

export const NavStack = styled(Stack)`
  button {
    border-radius: ${borderRadius(1)};
    align-items: center;
  }
`;

export const HeaderSection = styled(Stack)`
  padding: ${spacing4};
  border: 1px solid ${neutral200};
  border-radius: ${borderRadius(2)};

  color: ${default700};

  button {
    padding: 0;
  }

  ${HeadingS} {
    text-align: left;

    span {
      font-weight: 500;
    }
  }
`;

export const GuidanceContainer = styled.div<StyledDiv>`
  padding: ${spacing3};
  margin-bottom: ${spacing4};
  line-height: ${spacing6};

  color: ${default900};
  background: ${default200};
  border-radius: ${borderRadius(1)};

  ul {
    list-style-type: disc;
    list-style-position: outside;
  }

  ul li {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    margin-left: 20px;
    padding: 4px;
  }
`;

export const GuidanceTitle = styled.div<StyledDiv>`
  display: flex;
  align-items: center;
  font-size: ${fontSize1};
  font-weight: 700;

  span {
    margin-right: ${spacing2};
  }
`;
